// Ported verbatim from LC2 (src/features/coreApi/communitiesSlice.js), plus two new
// endpoints that aren't in LC2 at all — added for the new UI's header breadcrumb dropdown,
// which needs server-side search + pagination instead of the full client-side lists
// `getCommunitiesByAccountSfid`/entities.campaigns provide. Confirmed request shape (from
// live API calls): GET .../api/communities/?account_sfid=...&search=...&offset=0 and GET
// .../api/campaigns/?account_sfid=...&search=...&offset=0 — both confirmed to be core-api
// (BASE_API_URL) endpoints, NOT actions-api, even though campaignsSlice.js's existing
// rison-filtered `getCampaignsByAccountSfid` genuinely is on actions-api (different host,
// same-looking `campaigns/` path — don't conflate the two). Confirmed response shape
// carries its own pagination cursor, same as v2/residents/list/'s:
// {..., "pagination": {"limit": 100, "offset": 1, "total_count": 89060, "next_offset": 101}}
// — `next_offset` is what the backend wants sent as `offset` on the following request (not
// `offset + limit`, and not a client-incremented page index), and `total_count` is the real
// stopping signal — with counts in the tens of thousands, the dropdown has many pages left
// to fetch, so both are surfaced here instead of just the bare items array;
// SearchableBreadcrumbDropdown.js drives its next fetch off `nextOffset`/`totalCount`, not
// client-side arithmetic. Both endpoints pass `rawResponse: true` (see apiSlice.js's
// axiosBaseQuery) — without it, the base query's default `result.data.data` unwrap silently
// drops `pagination`, since it's a SIBLING of the response body's `data` key, not nested
// inside it; that's exactly why `next_offset` was always coming back null and the dropdown
// stopped after one page.
import {coreApiSlice} from './apiSlice';

const normalizeCampaign = (c) => ({
    ...c,
    sfid: c?.sfid || c?.id,
    property_sfid: c?.property_sfid || c?.rental_community?.sfid,
    name: c?.name || (c?.rental_community ? `${c.rental_community.name} - ${c.subtype}` : c?.subtype) || 'Untitled campaign'
});

// A malformed/unexpected response must never crash the dropdown — coerce anything that
// isn't already a real array into an empty one instead of letting `.map`/`.filter` throw.
const asArray = (value) => (Array.isArray(value) ? value : []);

// Shared response shape for both search endpoints below: the raw items (run through
// `normalize` for whichever entity this is) plus the backend's own pagination cursor.
const asSearchPage = (response, normalize) => {
    const rawList = asArray(Array.isArray(response) ? response : response?.results || response?.result || response?.data);
    const pagination = Array.isArray(response) ? null : response?.pagination || null;
    return {
        items: rawList.map(normalize).filter(Boolean),
        nextOffset: pagination?.next_offset ?? null,
        totalCount: pagination?.total_count ?? null
    };
};

export const communitiesApi = coreApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        searchCommunities: builder.query({
            query: ({accountSfid, search = '', offset = 0}) => ({
                url: 'communities/',
                method: 'get',
                params: {account_sfid: accountSfid, search, offset},
                skipAuthRedirect: true,
                rawResponse: true
            }),
            // The backend appends the account's child properties to every page without
            // applying `search` (or paging) to them, so a search for "grace" also returns
            // unrelated communities. Re-apply the search here so only real matches show.
            transformResponse: (response, _meta, arg) => {
                const needle = (arg?.search || '').trim().toLowerCase();
                const page = asSearchPage(response, (community) => (community?.sfid && community?.name ? community : null));
                return needle ? {...page, items: page.items.filter((community) => community.name.toLowerCase().includes(needle))} : page;
            },
            // Standard RTK Query "infinite list" pattern: one cache entry per
            // (account, search term), accumulating pages as `offset` grows instead of
            // replacing the list — offset 0 (a fresh search) always starts over.
            serializeQueryArgs: ({queryArgs}) => `${queryArgs.accountSfid}:${queryArgs.search}`,
            merge: (currentCache, newPage, {arg}) => {
                if (arg.offset === 0) return newPage;
                // Child properties come back on every page, so skip ones already listed.
                const seen = new Set(currentCache.items.map((item) => item.sfid));
                currentCache.items.push(...newPage.items.filter((item) => !seen.has(item.sfid)));
                currentCache.nextOffset = newPage.nextOffset;
                currentCache.totalCount = newPage.totalCount ?? currentCache.totalCount;
            },
            forceRefetch: ({currentArg, previousArg}) =>
                currentArg?.offset !== previousArg?.offset || currentArg?.search !== previousArg?.search
        }),
        searchCampaigns: builder.query({
            // CONFIRMED BACKEND BUG (not fixable from here) — this always 401s for a
            // normal logged-in session. Traced against the real deployed route in
            // stake-core-api: GET /api/campaigns/ (rewardsCampaignRoutes.py) is guarded by
            // @require_appkey (util.py), which checks ONLY a literal `dealsAPIKey` header
            // against a hardcoded shared secret and never looks at X-Session-Token at all
            // — it unconditionally abort(401)s without that header. Its sibling GET
            // /api/communities/ (rentalCommunityRoutes.py) correctly uses
            // @require_stytch_session_token instead, which does validate X-Session-Token,
            // which is why that one works. There is no header this frontend can legitimately
            // send to satisfy @require_appkey (the shared secret isn't meant to live in
            // client code), so this can't be worked around here — the backend route needs
            // to use @require_stytch_session_token like /api/communities/ does. Flagged
            // separately; `skipAuthRedirect` + the dropdown's inline error state keep this
            // from breaking anything else in the meantime.
            // The backend checks `account_sfid` before `community_sfid`, so when a community
            // is selected only that is sent — otherwise the list would be the whole account's.
            query: ({accountSfid, communitySfid, search = '', offset = 0}) => ({
                url: 'campaigns/',
                method: 'get',
                params: communitySfid ? {community_sfid: communitySfid, search, offset} : {account_sfid: accountSfid, search, offset},
                skipAuthRedirect: true,
                rawResponse: true
            }),
            transformResponse: (response) => asSearchPage(response, normalizeCampaign),
            serializeQueryArgs: ({queryArgs}) => `${queryArgs.accountSfid}:${queryArgs.communitySfid || 'all'}:${queryArgs.search}`,
            merge: (currentCache, newPage, {arg}) => {
                if (arg.offset === 0) return newPage;
                currentCache.items.push(...newPage.items);
                currentCache.nextOffset = newPage.nextOffset;
                currentCache.totalCount = newPage.totalCount ?? currentCache.totalCount;
            },
            forceRefetch: ({currentArg, previousArg}) =>
                currentArg?.offset !== previousArg?.offset ||
                currentArg?.search !== previousArg?.search ||
                currentArg?.communitySfid !== previousArg?.communitySfid
        }),
        getCommunitiesByAccountSfid: builder.query({
            query: (sfid) => ({
                url: 'rental_community/list/',
                method: 'get',
                params: {account_id: sfid}
            }),
            providesTags: [{type: 'Communities', id: 'LIST'}],
            transformResponse: (response) => {
                const validCommunities = response
                    .filter((community) => community.sfid && community.name)
                    .sort((a, b) => a.name.localeCompare(b.name));
                return validCommunities;
            }
        }),
        getCommunityBySfid: builder.query({
            query: (sfid) => ({
                url: 'rental_community/',
                method: 'get',
                params: {sfid}
            }),
            providesTags: (_res, err, arg) => !err && [{type: 'Communities', id: arg}]
        }),
        getCommunityById: builder.query({
            query: (id) => ({
                url: 'rental_community/',
                method: 'get',
                params: {id}
            }),
            providesTags: (_res, err, arg) => !err && [{type: 'Communities', id: arg}]
        }),
        addCommunity: builder.mutation({
            query: (data) => ({
                url: 'rental_community/add/',
                method: 'post',
                data,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }),
            invalidatesTags: [
                {type: 'Communities', id: 'LIST'},
                {type: 'Glance', id: 'LIST'}
            ]
        }),
        editCommunity: builder.mutation({
            query: ({id, data}) => ({
                url: `rental_community/${id}/update_from_lc_calculator_data/`,
                method: 'patch',
                data
            })
        })
    }),
    overrideExisting: false
});

export const {
    useSearchCommunitiesQuery,
    useSearchCampaignsQuery,
    useLazyGetCommunitiesByAccountSfidQuery,
    useLazyGetCommunityBySfidQuery,
    useAddCommunityMutation,
    useEditCommunityMutation,
    useLazyGetCommunityByIdQuery
} = communitiesApi;
